/**
 * Task Auto-Complete Service
 * FinTasks 2.0 - Phase 2
 * 
 * Handles automatic task completion based on financial events.
 * When a user performs an action (pays a bill, logs income, etc.),
 * related tasks are automatically marked as complete.
 * 
 * SAFETY RULES:
 * - This service ONLY completes tasks, it does NOT modify other services
 * - All event detection is done through callbacks, not by modifying source services
 * - Completion is reversible (user can reopen tasks)
 * 
 * @version 2.0.0 - Phase 2
 * @date 2024-12-04
 */

import { TASK_STATUS } from '@/services/finTasksService';
import { 
  findTasksToAutoComplete, 
  SMART_TASK_TYPE,
  SMART_TASK_TRIGGER 
} from '@/services/smartTasksService';

// ============================================================================
// EVENT TYPES
// ============================================================================

export const FINANCIAL_EVENT = {
  // Transaction events
  TRANSACTION_CREATED: 'transaction_created',
  INCOME_RECEIVED: 'income_received',
  EXPENSE_LOGGED: 'expense_logged',
  
  // Budget events
  BUDGET_UPDATED: 'budget_updated',
  BUDGET_ALLOCATED: 'budget_allocated',
  ENVELOPE_FUNDED: 'envelope_funded',
  
  // Bill events
  BILL_PAID: 'bill_paid',
  BILL_SCHEDULED: 'bill_scheduled',
  
  // Goal events
  GOAL_CONTRIBUTION: 'goal_contribution',
  GOAL_COMPLETED: 'goal_completed',
  
  // Subscription events
  SUBSCRIPTION_REVIEWED: 'subscription_reviewed',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled'
};

// ============================================================================
// AUTO-COMPLETE MATCHERS
// ============================================================================

/**
 * Match tasks that should complete when a transaction is created
 * 
 * @param {Object} task - Task to check
 * @param {Object} transaction - The transaction that was created
 * @returns {boolean} Whether the task should be completed
 */
export function matchTransactionCreated(task, transaction) {
  // Must be a financial task
  if (!task.isFinancialTask) return false;
  
  // Must not already be complete
  if (task.status === TASK_STATUS.DONE) return false;

  // Match by bill ID
  if (task.relatedBillId && task.relatedBillId === transaction.billId) {
    return true;
  }

  // Match by category for bill payment tasks
  if (
    task.smartTaskType === SMART_TASK_TYPE.UPCOMING_BILL &&
    task.relatedCategoryId === transaction.categoryId &&
    transaction.type === 'expense'
  ) {
    return true;
  }

  // Match income allocation tasks
  if (
    task.smartTaskType === SMART_TASK_TYPE.INCOME_ALLOCATION &&
    task.relatedTransactionId === transaction.id
  ) {
    return true;
  }

  return false;
}

/**
 * Match tasks that should complete when income is received
 * 
 * @param {Object} task - Task to check
 * @param {Object} income - The income transaction
 * @returns {boolean} Whether the task should be completed
 */
export function matchIncomeReceived(task, income) {
  if (!task.isFinancialTask) return false;
  if (task.status === TASK_STATUS.DONE) return false;

  // Match salary received tasks
  if (
    task.smartTaskType === SMART_TASK_TYPE.SALARY_RECEIVED &&
    task.autoCompleteOn === 'income_received'
  ) {
    return true;
  }

  return false;
}

/**
 * Match tasks that should complete when budget is allocated
 * 
 * @param {Object} task - Task to check
 * @param {Object} allocation - The allocation event
 * @returns {boolean} Whether the task should be completed
 */
export function matchBudgetAllocated(task, allocation) {
  if (!task.isFinancialTask) return false;
  if (task.status === TASK_STATUS.DONE) return false;

  // Match income allocation tasks
  if (
    task.smartTaskType === SMART_TASK_TYPE.INCOME_ALLOCATION &&
    task.autoCompleteOn === 'budget_allocated'
  ) {
    return true;
  }

  // Match allocate budget tasks
  if (
    task.smartTaskType === SMART_TASK_TYPE.ALLOCATE_BUDGET &&
    task.relatedBudgetId === allocation.budgetId
  ) {
    return true;
  }

  return false;
}

/**
 * Match tasks that should complete when a goal contribution is made
 * 
 * @param {Object} task - Task to check
 * @param {Object} contribution - The goal contribution
 * @returns {boolean} Whether the task should be completed
 */
export function matchGoalContribution(task, contribution) {
  if (!task.isFinancialTask) return false;
  if (task.status === TASK_STATUS.DONE) return false;

  // Match goal behind tasks
  if (
    task.smartTaskType === SMART_TASK_TYPE.GOAL_BEHIND &&
    (task.relatedGoalId === contribution.goalId || task.goalId === contribution.goalId)
  ) {
    return true;
  }

  // Match goal contribution tasks
  if (
    task.smartTaskType === SMART_TASK_TYPE.GOAL_CONTRIBUTION &&
    (task.relatedGoalId === contribution.goalId || task.goalId === contribution.goalId)
  ) {
    return true;
  }

  return false;
}

/**
 * Match tasks that should complete when a budget is adjusted
 * 
 * @param {Object} task - Task to check
 * @param {Object} budgetUpdate - The budget update
 * @returns {boolean} Whether the task should be completed
 */
export function matchBudgetAdjusted(task, budgetUpdate) {
  if (!task.isFinancialTask) return false;
  if (task.status === TASK_STATUS.DONE) return false;

  // Match overspend review tasks
  if (
    task.smartTaskType === SMART_TASK_TYPE.OVERSPEND_REVIEW &&
    task.relatedBudgetId === budgetUpdate.budgetId
  ) {
    return true;
  }

  // Match budget warning tasks
  if (
    task.smartTaskType === SMART_TASK_TYPE.BUDGET_WARNING &&
    task.relatedBudgetId === budgetUpdate.budgetId
  ) {
    return true;
  }

  return false;
}

// ============================================================================
// MAIN AUTO-COMPLETE FUNCTION
// ============================================================================

/**
 * Process a financial event and find all tasks that should be auto-completed
 * 
 * @param {string} eventType - The type of financial event (from FINANCIAL_EVENT)
 * @param {Object} eventData - The event data
 * @param {Array} tasks - All current tasks
 * @returns {Array} Array of task IDs that should be completed
 */
export function processFinancialEvent(eventType, eventData, tasks) {
  const tasksToComplete = [];

  for (const task of tasks) {
    let shouldComplete = false;

    switch (eventType) {
      case FINANCIAL_EVENT.TRANSACTION_CREATED:
      case FINANCIAL_EVENT.EXPENSE_LOGGED:
        shouldComplete = matchTransactionCreated(task, eventData);
        break;

      case FINANCIAL_EVENT.INCOME_RECEIVED:
        shouldComplete = matchIncomeReceived(task, eventData);
        break;

      case FINANCIAL_EVENT.BUDGET_ALLOCATED:
      case FINANCIAL_EVENT.ENVELOPE_FUNDED:
        shouldComplete = matchBudgetAllocated(task, eventData);
        break;

      case FINANCIAL_EVENT.GOAL_CONTRIBUTION:
        shouldComplete = matchGoalContribution(task, eventData);
        break;

      case FINANCIAL_EVENT.BUDGET_UPDATED:
        shouldComplete = matchBudgetAdjusted(task, eventData);
        break;

      case FINANCIAL_EVENT.BILL_PAID:
        shouldComplete = matchTransactionCreated(task, eventData);
        break;

      default:
        break;
    }

    if (shouldComplete) {
      tasksToComplete.push({
        taskId: task.id,
        listId: task.listId,
        reason: `Auto-completed by ${eventType}`,
        eventData
      });
    }
  }

  return tasksToComplete;
}

// ============================================================================
// AUTO-COMPLETE EXECUTOR
// ============================================================================

/**
 * Execute auto-completion for a list of tasks
 * This is called by FinTasksContext when a financial event occurs
 * 
 * @param {Array} tasksToComplete - Array of {taskId, listId, reason} objects
 * @param {Function} completeTaskFn - Function to complete a task (from context)
 * @returns {Promise<Object>} Results of the auto-completion
 */
export async function executeAutoComplete(tasksToComplete, completeTaskFn) {
  const results = {
    completed: [],
    failed: [],
    skipped: []
  };

  for (const { taskId, listId, reason, eventData } of tasksToComplete) {
    try {
      // Complete the task
      await completeTaskFn({ id: taskId, listId });
      
      results.completed.push({
        taskId,
        reason,
        completedAt: new Date().toISOString()
      });

      console.log(`[TaskAutoComplete] Auto-completed task ${taskId}: ${reason}`);
    } catch (error) {
      console.error(`[TaskAutoComplete] Failed to auto-complete task ${taskId}:`, error);
      
      results.failed.push({
        taskId,
        reason,
        error: error.message
      });
    }
  }

  return results;
}

// ============================================================================
// EVENT EMISSION HELPERS
// ============================================================================

/**
 * Create an event object for use with processFinancialEvent
 * 
 * @param {string} type - Event type from FINANCIAL_EVENT
 * @param {Object} data - Event-specific data
 * @returns {Object} Formatted event object
 */
export function createFinancialEvent(type, data) {
  return {
    type,
    data,
    timestamp: new Date().toISOString(),
    id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  };
}

/**
 * Create a transaction event
 */
export function createTransactionEvent(transaction) {
  const isIncome = transaction.type === 'income';
  return createFinancialEvent(
    isIncome ? FINANCIAL_EVENT.INCOME_RECEIVED : FINANCIAL_EVENT.TRANSACTION_CREATED,
    transaction
  );
}

/**
 * Create a budget update event
 */
export function createBudgetEvent(budgetUpdate) {
  return createFinancialEvent(FINANCIAL_EVENT.BUDGET_UPDATED, budgetUpdate);
}

/**
 * Create a goal contribution event
 */
export function createGoalContributionEvent(contribution) {
  return createFinancialEvent(FINANCIAL_EVENT.GOAL_CONTRIBUTION, contribution);
}

/**
 * Create an envelope allocation event
 */
export function createEnvelopeEvent(envelopeUpdate) {
  return createFinancialEvent(FINANCIAL_EVENT.ENVELOPE_FUNDED, envelopeUpdate);
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // Event types
  FINANCIAL_EVENT,
  
  // Matchers
  matchTransactionCreated,
  matchIncomeReceived,
  matchBudgetAllocated,
  matchGoalContribution,
  matchBudgetAdjusted,
  
  // Processing
  processFinancialEvent,
  executeAutoComplete,
  
  // Event creation helpers
  createFinancialEvent,
  createTransactionEvent,
  createBudgetEvent,
  createGoalContributionEvent,
  createEnvelopeEvent
};
